import { useQuery } from "@apollo/client";
import { MembershipStatus } from "@app/shared/constants";
import { Membership, MembershipBillingChoice, MembershipBillingPlan } from "@app/shared/types";
import { GRAPHQL_QUERY_MEMBERSHIPS, GRAPHQL_QUERY_SIGN_UP_BILLING_PLAN_OPTIONS } from "app/queries";
import { routes } from "app/routes";
import { GenericErrorPage } from "components/GenericErrorPage";
import LoadingPage from "features/pages/LoadingPage";
import { RequireAccountCreation } from "features/signup/RequireAccountCreation";
import { useQueryParams } from "hooks/useQueryParams";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Redirect } from "react-router";
import { SignupBillingPricePage } from "./SignupBillingPricePage";
import { SESSION_STORAGE_PLAN_NAME_KEY } from "./SignupFlow";
import { getSignupAnalyticsMetadataFromPlanName } from "./signupHelpers";
import { StripeClientSecretProvider } from "./StripeClientSecretProvider";
import { SignUpStepProps } from "./types";

export const SESSION_STORAGE_SCHOLARSHIP_APPLICATION_ID_KEY = "signUpFlowScholarshipApplicationId";

enum BillingStep {
    BILLING_PLAN,
    DONATION,
    FREETRIAL,
    CREATE_ACCOUNT,
}

export const SignupBillingStep = (props: SignUpStepProps) => {
    const [billingChoice, setBillingChoice] = useState<MembershipBillingChoice | undefined>(
        undefined,
    );

    const [step, setStep] = useState(BillingStep.BILLING_PLAN);

    const { data: queryData } = useQuery(GRAPHQL_QUERY_SIGN_UP_BILLING_PLAN_OPTIONS);
    const billingPlanOptions: MembershipBillingPlan[] | undefined =
        queryData?.signupBillingPlanOptions.filter((x: MembershipBillingPlan) => x.available);

    const [defaultPlan, setDefaultPlan] = useState<MembershipBillingChoice | undefined>(undefined);

    const preselectedPlanName = sessionStorage.getItem(SESSION_STORAGE_PLAN_NAME_KEY) || undefined;
    const analyticsData = getSignupAnalyticsMetadataFromPlanName(preselectedPlanName);
    const query = useQueryParams();

    const [allowedEmail] = useState(query.email as string | undefined);

    useEffect(() => {
        if (billingPlanOptions && step === BillingStep.BILLING_PLAN) {
            const preselectedPlan = billingPlanOptions.find(
                (x) => x.planName === props.preselectedPlanName,
            );
            if (!billingChoice) {
                // default to the monthly billing cycle, if no plan is preselected
                const defaultPlan =
                    billingPlanOptions.find((x) => x.cycle === "month") ?? billingPlanOptions[0];
                setDefaultPlan({
                    plan: defaultPlan,
                    donationAmount: 0,
                });

                if (preselectedPlan) {
                    setBillingChoice({
                        plan: preselectedPlan,
                        donationAmount: 0,
                    });
                    // Skip the billing choice step if a plan was preselected, and skip donation if it is a discounted plan
                    setStep(
                        preselectedPlan.discounted
                            ? BillingStep.CREATE_ACCOUNT
                            : BillingStep.DONATION,
                    );
                }
            }
        }
    }, [billingPlanOptions, billingChoice, props.preselectedPlanName]);

    // If the user has any active/pending memberships redirect to the member zone
    const { data: membershipData, loading: membershipsLoading } =
        useQuery(GRAPHQL_QUERY_MEMBERSHIPS);
    const memberships = (membershipData?.myMemberships || []) as Membership[];
    const activeOrPending = memberships.filter((x) =>
        _.includes(MembershipStatus.AllActiveOrPending, x.status),
    );
    if (activeOrPending.length > 0) {
        return <Redirect to={routes.memberZone()} />;
    }

    if (membershipsLoading || !billingPlanOptions || !defaultPlan) {
        return <LoadingPage />;
    }

    const onSetDonation = (donationAmount: number) => {
        if (!billingChoice) {
            throw new Error("Billing plan not set");
        }

        setBillingChoice({
            plan: billingChoice.plan,
            donationAmount,
        });
        setStep(BillingStep.CREATE_ACCOUNT);
    };

    const renderCheckout = () => {
        if (billingChoice) {
            return (
                <RequireAccountCreation
                    analyticsPrefix="signup"
                    analyticsData={analyticsData}
                    allowedEmail={allowedEmail}
                    optimizelyEventKey="explorerSignupViewed"
                >
                    <StripeClientSecretProvider billingChoice={billingChoice} />
                </RequireAccountCreation>
            );
        } else {
            return <GenericErrorPage />;
        }
    };

    switch (step) {
        case BillingStep.CREATE_ACCOUNT:
            return renderCheckout();
        default:
            return (
                <SignupBillingPricePage
                    billingPlanOptions={billingPlanOptions}
                    billingPlan={billingChoice?.plan}
                    onChangeBillingPlan={(plan) => setBillingChoice({ plan, donationAmount: 0 })}
                    onContinue={() => {
                        setStep(BillingStep.CREATE_ACCOUNT);
                    }}
                />
            );
    }
};
